import { on } from "@ngrx/store";
import { Version } from "src/api-service";
import { ArticlesActions, ArticleTableFilterActions, ProductCatalogActions } from "./articles.actions";
import { QuoteState } from "../quote.state";
import { initialArticlesFilterState, initialProductCatalogFilterState } from "./articles.state";
import { PagesStore } from "../../pages.state";

function updateSelectedVersion(quoteState: QuoteState, updater: (version: Version) => Version): QuoteState {
    return {
        ...quoteState,
        versions: quoteState.versions.map(v => {
            if (v.id !== quoteState.selectedVersion) return v;
            let updated = updater(v);
            if (updated.summary && updated.summary !== v.summary) {
                if (updated.projectManagementAndKickback && updated.summary.kickbackExVat !== v.summary?.kickbackExVat) {
                    updated = { ...updated, projectManagementAndKickback: { ...updated.projectManagementAndKickback, kickbackExVat: updated.summary.kickbackExVat } };
                }
                if (updated.projectDiscount && updated.summary.projectDiscountAmount !== v.summary?.projectDiscountAmount) {
                    updated = { ...updated, projectDiscount: { ...updated.projectDiscount, projectDiscountAmount: updated.summary.projectDiscountAmount } };
                }
            }
            return updated;
        }),
    };
}

// **************************************************************************
// ARTICLE OPERATION REDUCERS
// **************************************************************************

const articleOperationSuccess = on(
    ArticlesActions.createArticleSuccess, ArticlesActions.batchCreateArticlesSuccess, ArticlesActions.batchChangeCatalogProductsSuccess,
    ArticlesActions.batchDeleteArticlesSuccess, ArticlesActions.updateArticleFieldSuccess, ArticlesActions.deleteArticleSuccess,
    ArticlesActions.refreshArticlesSuccess, ArticlesActions.duplicateArticleSuccess, ArticlesActions.duplicateArticleGroupSuccess,
    ArticlesActions.pasteArticleSuccess, ArticlesActions.moveArticleSuccess, ArticlesActions.uploadArticleImageSuccess,
    ArticlesActions.convertArticleSuccess, ArticlesActions.importArticlesSuccess,
    (state: PagesStore, { response }) => ({
        ...state,
        quote: updateSelectedVersion(state.quote, v => ({
            ...v,
            itemGroups: response.itemGroups,
            latestOperation: response.latestOperation,
            summary: response.summary ?? v.summary,
            vendorSummaries: response.vendorSummaries ?? v.vendorSummaries,
            subscription: response.subscriptionVersion ?? v.subscription,
        })),
    }),
);

const articleOperationFailure = on(
    ArticlesActions.createArticleFailure, ArticlesActions.batchCreateArticlesFailure, ArticlesActions.batchChangeCatalogProductsFailure,
    ArticlesActions.batchDeleteArticlesFailure, ArticlesActions.updateArticleFieldFailure, ArticlesActions.deleteArticleFailure,
    ArticlesActions.refreshArticlesFailure, ArticlesActions.duplicateArticleFailure, ArticlesActions.duplicateArticleGroupFailure,
    ArticlesActions.pasteArticleFailure, ArticlesActions.moveArticleFailure, ArticlesActions.uploadArticleImageFailure,
    ArticlesActions.convertArticleFailure, ArticlesActions.importArticlesFailure,
    (state: PagesStore, { response }) => ({
        ...state,
        quote: updateSelectedVersion(state.quote, v => ({ ...v, latestOperation: response.latestOperation })),
    }),
);

// **************************************************************************
// ARTICLE TABLE FILTER REDUCERS
// **************************************************************************

const toggleFrozenColumn = on(
    ArticleTableFilterActions.toggleFrozenColumn,
    (state: PagesStore, { columnKey }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                frozenColumns: {
                    ...state.quote.articlesFilter.frozenColumns,
                    [columnKey]: !state.quote.articlesFilter.frozenColumns[columnKey],
                },
            },
        },
    }),
);

const setFrozenColumns = on(
    ArticleTableFilterActions.setFrozenColumns,
    (state: PagesStore, { frozenColumns }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: { ...state.quote.articlesFilter, frozenColumns },
        },
    }),
);

const setColumnWidth = on(
    ArticleTableFilterActions.setColumnWidth,
    (state: PagesStore, { columnKey, width }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                columnWidths: { ...state.quote.articlesFilter.columnWidths, [columnKey]: width },
            },
        },
    }),
);

const setColumnOrder = on(
    ArticleTableFilterActions.setColumnOrder,
    (state: PagesStore, { columnOrder }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: { ...state.quote.articlesFilter, columnOrder },
        },
    }),
);

const setItemVisibleColumns = on(
    ArticleTableFilterActions.setVisibleColumns,
    (state: PagesStore, { visibleColumns }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: { ...state.quote.articlesFilter, visibleColumns },
        },
    }),
);

const resetItemFilter = on(
    ArticleTableFilterActions.resetFilter,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: initialArticlesFilterState,
        },
    }),
);

const loadItemFilter = on(
    ArticleTableFilterActions.loadFilter,
    (state: PagesStore, { filter }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: filter,
        },
    }),
);

const setGlobalBrandFilter = on(
    ArticleTableFilterActions.setGlobalBrandFilter,
    (state: PagesStore, { brands }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: { ...state.quote.articlesFilter, globalBrandFilter: brands },
        },
    }),
);

const clearGlobalBrandFilter = on(
    ArticleTableFilterActions.clearGlobalBrandFilter,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: { ...state.quote.articlesFilter, globalBrandFilter: [] },
        },
    }),
);

const setGroupBrandFilter = on(
    ArticleTableFilterActions.setGroupBrandFilter,
    (state: PagesStore, { groupId, brands }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                groupBrandFilters: { ...state.quote.articlesFilter.groupBrandFilters, [groupId]: brands },
            },
        },
    }),
);

const clearGroupBrandFilter = on(
    ArticleTableFilterActions.clearGroupBrandFilter,
    (state: PagesStore, { groupId }) => {
        const { [groupId]: _, ...rest } = state.quote.articlesFilter.groupBrandFilters;
        return {
            ...state,
            quote: {
                ...state.quote,
                articlesFilter: { ...state.quote.articlesFilter, groupBrandFilters: rest },
            },
        };
    },
);

const setArticleBrandFilter = on(
    ArticleTableFilterActions.setArticleBrandFilter,
    (state: PagesStore, { articleId, brands }) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                itemBrandFilters: { ...state.quote.articlesFilter.itemBrandFilters, [articleId]: brands },
            },
        },
    }),
);

const clearArticleBrandFilter = on(
    ArticleTableFilterActions.clearArticleBrandFilter,
    (state: PagesStore, { articleId }) => {
        const { [articleId]: _, ...rest } = state.quote.articlesFilter.itemBrandFilters;
        return {
            ...state,
            quote: {
                ...state.quote,
                articlesFilter: { ...state.quote.articlesFilter, itemBrandFilters: rest },
            },
        };
    },
);

const clearAllBrandFilters = on(
    ArticleTableFilterActions.clearAllBrandFilters,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                globalBrandFilter: [],
                groupBrandFilters: {},
                itemBrandFilters: {},
            },
        },
    }),
);

const expandAllGroups = on(
    ArticleTableFilterActions.expandAllGroups,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                expandCommand: { type: 'expand' as const, requestId: Date.now() },
            },
        },
    }),
);

const collapseAllGroups = on(
    ArticleTableFilterActions.collapseAllGroups,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            articlesFilter: {
                ...state.quote.articlesFilter,
                expandCommand: { type: 'collapse' as const, requestId: Date.now() },
            },
        },
    }),
);

// **************************************************************************
// PRODUCT CATALOG REDUCERS
// **************************************************************************

const loadCatalogsSuccess = on(
    ProductCatalogActions.loadSuccess,
    (state: PagesStore, { result }) => ({
        ...state,
        quote: { ...state.quote, catalogs: result.catalogs ?? [] },
    }),
);

const loadCatalogsFailure = on(
    ProductCatalogActions.loadFailure,
    (state: PagesStore) => ({
        ...state,
        quote: { ...state.quote, catalogs: [] },
    }),
);

const clearCatalogs = on(
    ProductCatalogActions.clear,
    (state: PagesStore) => ({
        ...state,
        quote: { ...state.quote, catalogs: [] },
    }),
);

const updateCatalogFilter = on(
    ProductCatalogActions.updateFilter,
    (state: PagesStore, { filter }) => ({
        ...state,
        quote: {
            ...state.quote,
            catalogFilter: { ...state.quote.catalogFilter, ...filter },
        },
    }),
);

const resetCatalogFilter = on(
    ProductCatalogActions.resetFilter,
    (state: PagesStore) => ({
        ...state,
        quote: { ...state.quote, catalogFilter: initialProductCatalogFilterState },
    }),
);

const loadCatalogsLoading = on(
    ProductCatalogActions.load,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            loading: { ...state.quote.loading, catalogs: true },
        },
    }),
);

const resetCatalogsLoading = on(
    ProductCatalogActions.loadSuccess,
    ProductCatalogActions.loadFailure,
    ProductCatalogActions.blocked,
    ProductCatalogActions.clear,
    (state: PagesStore) => ({
        ...state,
        quote: {
            ...state.quote,
            loading: { ...state.quote.loading, catalogs: false },
        },
    }),
);

// **************************************************************************
// EXPORT ALL REDUCERS
// **************************************************************************

export const articlesReducer = [
    // Articles
    articleOperationSuccess,
    articleOperationFailure,
    // Article Table Filter
    toggleFrozenColumn,
    setFrozenColumns,
    setColumnWidth,
    setColumnOrder,
    setItemVisibleColumns,
    resetItemFilter,
    loadItemFilter,
    setGlobalBrandFilter,
    clearGlobalBrandFilter,
    setGroupBrandFilter,
    clearGroupBrandFilter,
    setArticleBrandFilter,
    clearArticleBrandFilter,
    clearAllBrandFilters,
    expandAllGroups,
    collapseAllGroups,
    // Product Catalog
    loadCatalogsSuccess,
    loadCatalogsFailure,
    clearCatalogs,
    updateCatalogFilter,
    resetCatalogFilter,
    loadCatalogsLoading,
    resetCatalogsLoading,
];
